/**
 * DIN Watchlist — saved suspicious DINs + auto-alert delta view
 * =============================================================
 * Lists watched DINs, latest scan timestamp, "Scan Now" + "Scan All" buttons,
 * and a recent alerts feed showing newly-joined companies and status changes.
 */
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Eye, Bell, BellRing, Trash2, RefreshCw, Loader2, AlertTriangle,
  ShieldAlert, CheckCircle2, Clock, Building2, X, Plus, ArrowUpRight,
} from "lucide-react";

const API = `${window.location.origin}/api/mca21/din-watchlist`;

const SEVERITY_COLOR = { LOW: "#22c55e", MEDIUM: "#f59e0b", HIGH: "#ef4444", CRITICAL: "#dc2626" };
const CARD = { background: "#0d1117", border: "1px solid #1e2530", borderRadius: 12, padding: 18 };
const LABEL = { color: "#64748b", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6 };

export default function DINWatchlistPage() {
  const [items, setItems] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [scanningOne, setScanningOne] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newDin, setNewDin] = useState("");
  const [newName, setNewName] = useState("");
  const [newReason, setNewReason] = useState("");
  const [newSeverity, setNewSeverity] = useState("MEDIUM");
  const [adding, setAdding] = useState(false);
  const navigate = useNavigate();

  const fetchAll = useCallback(async () => {
    try {
      const [w, a] = await Promise.all([
        axios.get(API, { withCredentials: true }),
        axios.get(`${API}/alerts?limit=20`, { withCredentials: true }),
      ]);
      setItems(w.data.watchlist || []);
      setAlerts(a.data.alerts || []);
    } catch (err) { console.error("[DINWatchlist] Load failed:", err); } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const addDin = async () => {
    const din = newDin.trim();
    if (!/^\d{8}$/.test(din)) {
      toast.error("DIN must be exactly 8 digits");
      return;
    }
    setAdding(true);
    try {
      await axios.post(API, { din, name: newName.trim() || null, reason: newReason.trim() || null, severity: newSeverity }, { withCredentials: true });
      toast.success(`DIN ${din} added to watchlist`);
      setNewDin(""); setNewName(""); setNewReason(""); setNewSeverity("MEDIUM");
      setShowAddForm(false);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add DIN");
    } finally { setAdding(false); }
  };

  const removeDin = async (din) => {
    if (!window.confirm(`Remove DIN ${din} from watchlist?`)) return;
    try {
      await axios.delete(`${API}/${din}`, { withCredentials: true });
      toast.success("Removed");
      fetchAll();
    } catch { toast.error("Could not remove"); }
  };

  const scanOne = async (din) => {
    setScanningOne(din);
    try {
      const { data } = await axios.post(`${API}/${din}/scan`, {}, { withCredentials: true });
      const newCount = data.delta?.new_companies?.length || 0;
      const sChange = data.delta?.status_changes?.length || 0;
      if (newCount + sChange > 0) toast.success(`Alert! ${newCount} new co + ${sChange} status changes`);
      else toast.success("No changes — clean");
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Scan failed"); }
    finally { setScanningOne(null); }
  };

  const scanAll = async () => {
    setScanning(true);
    try {
      const { data } = await axios.post(`${API}/scan-all`, {}, { withCredentials: true });
      toast.success(`Scanned ${data.scanned} DINs — ${data.alerts_triggered} alert(s) triggered`);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Bulk scan failed"); }
    finally { setScanning(false); }
  };

  const ackAlert = async (id) => {
    try {
      await axios.post(`${API}/alerts/${id}/acknowledge`, {}, { withCredentials: true });
      fetchAll();
    } catch { toast.error("Could not acknowledge"); }
  };

  const unackCount = alerts.filter(a => !a.acknowledged).length;

  if (loading) return <div style={{ padding: 60, textAlign: "center" }}><Loader2 className="animate-spin" size={24} style={{ color: "#94a3b8" }} /></div>;

  return (
    <div data-testid="din-watchlist-page" style={{ color: "#f1f5f9" }}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6 flex-wrap" style={{ gap: 12 }}>
        <div className="flex items-center gap-3">
          <Eye style={{ width: 26, height: 26, color: "#ef4444" }} />
          <div>
            <h1 style={{ fontSize: 26, fontWeight: 700, margin: 0 }} data-testid="din-watchlist-title">DIN Watchlist</h1>
            <p style={{ color: "#94a3b8", fontSize: 12, margin: 0, marginTop: 2 }}>
              Auto-monitored suspicious DINs · alerts when they join new companies · scanned every 24h
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="scan-all-btn" onClick={scanAll} disabled={scanning || items.length === 0} style={{ ...ghostBtn, color: "#3b82f6", borderColor: "#3b82f655" }}>
            {scanning ? <Loader2 className="animate-spin" size={13} /> : <RefreshCw size={13} />} Scan All
          </button>
          <button data-testid="add-din-btn" onClick={() => setShowAddForm(s => !s)} style={primaryBtn}>
            <Plus size={13} /> Add DIN
          </button>
        </div>
      </div>

      {/* Stats */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 12, marginBottom: 18 }}>
        <Stat icon={Eye} label="Watched DINs" value={items.length} color="#a855f7" testid="stat-watched" />
        <Stat icon={BellRing} label="Open Alerts" value={unackCount} color="#ef4444" testid="stat-open-alerts" />
        <Stat icon={Bell} label="Total Alerts" value={alerts.length} color="#f59e0b" testid="stat-total-alerts" />
        <Stat icon={ShieldAlert} label="CRITICAL" value={items.filter(i => i.severity === "CRITICAL").length} color="#dc2626" testid="stat-critical" />
      </div>

      {/* Add form */}
      {showAddForm && (
        <div style={{ ...CARD, marginBottom: 18 }} data-testid="add-din-form">
          <h3 style={{ fontSize: 14, fontWeight: 700, marginTop: 0, marginBottom: 12 }}>Add DIN to Watchlist</h3>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 120px", gap: 10, marginBottom: 10 }}>
            <Input label="DIN (8 digits)" value={newDin} onChange={v => setNewDin(v.replace(/\D/g, "").slice(0, 8))} placeholder="08456321" mono testid="add-din-input" />
            <Input label="Name (optional)" value={newName} onChange={setNewName} placeholder="RAJESH KUMAR SHARMA" testid="add-din-name" />
            <Input label="Reason" value={newReason} onChange={setNewReason} placeholder="Linked to struck-off shell co" testid="add-din-reason" />
            <div>
              <div style={LABEL}>Severity</div>
              <select data-testid="add-din-severity" value={newSeverity} onChange={e => setNewSeverity(e.target.value)} style={{ ...inputStyle, marginTop: 4 }}>
                <option value="LOW">LOW</option><option value="MEDIUM">MEDIUM</option>
                <option value="HIGH">HIGH</option><option value="CRITICAL">CRITICAL</option>
              </select>
            </div>
          </div>
          <button data-testid="confirm-add-din" onClick={addDin} disabled={adding || !/^\d{8}$/.test(newDin)} style={primaryBtn}>
            {adding ? <Loader2 className="animate-spin" size={13} /> : <Plus size={13} />} Save (fetches snapshot)
          </button>
          <button onClick={() => setShowAddForm(false)} style={{ ...ghostBtn, marginLeft: 6 }}>Cancel</button>
        </div>
      )}

      {/* Alerts feed */}
      {alerts.length > 0 && (
        <div style={{ ...CARD, marginBottom: 18 }}>
          <h2 style={{ fontSize: 13, fontWeight: 700, marginTop: 0, marginBottom: 12, display: "flex", alignItems: "center", gap: 6 }}>
            <BellRing size={14} style={{ color: unackCount > 0 ? "#ef4444" : "#94a3b8" }} /> Recent Alerts ({unackCount} unack)
          </h2>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {alerts.slice(0, 8).map(a => (
              <div key={a.id} data-testid={`alert-${a.id}`} style={{ background: a.acknowledged ? "#080c12" : "rgba(239,68,68,0.06)", border: `1px solid ${a.acknowledged ? "#1e2530" : "#ef444455"}`, borderRadius: 8, padding: 12 }}>
                <div className="flex items-center justify-between flex-wrap" style={{ gap: 8 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <code style={{ color: "#a855f7", fontSize: 11 }}>DIN {a.din}</code>
                      <span style={{ fontSize: 12, fontWeight: 600 }}>{a.din_name || "—"}</span>
                      <span style={pill(SEVERITY_COLOR[a.severity] || "#94a3b8")}>{a.severity}</span>
                      {!a.acknowledged && <span style={pill("#ef4444")}>NEW</span>}
                    </div>
                    <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 4 }}>
                      {a.new_companies?.length > 0 && <span style={{ color: "#ef4444" }}>{a.new_companies.length} new co</span>}
                      {a.new_companies?.length > 0 && a.status_changes?.length > 0 && " · "}
                      {a.status_changes?.length > 0 && <span style={{ color: "#f59e0b" }}>{a.status_changes.length} status change</span>}
                      <span style={{ marginLeft: 8, color: "#64748b" }}>{new Date(a.scanned_at).toLocaleString()}</span>
                    </div>
                    {a.new_companies?.length > 0 && (
                      <div style={{ marginTop: 6, fontSize: 11, color: "#cbd5e1" }}>
                        New: {a.new_companies.slice(0, 3).map(c => c.company_name || c.cin).join(", ")}{a.new_companies.length > 3 ? ` +${a.new_companies.length - 3}` : ""}
                      </div>
                    )}
                  </div>
                  {!a.acknowledged && (
                    <button onClick={() => ackAlert(a.id)} data-testid={`ack-alert-${a.id}`} style={{ ...ghostBtn, color: "#22c55e", borderColor: "#22c55e55" }}>
                      <CheckCircle2 size={11} /> Ack
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* DIN list */}
      <div style={CARD}>
        <h2 style={{ fontSize: 13, fontWeight: 700, marginTop: 0, marginBottom: 12 }}>Watchlist ({items.length})</h2>
        {items.length === 0 ? (
          <div style={{ padding: 40, textAlign: "center", color: "#64748b", fontSize: 12 }}>
            <Eye size={28} style={{ margin: "0 auto 8px", display: "block", color: "#475569" }} />
            No DINs watched yet. Add one above, or search a director and add from results.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {items.map(i => {
              const c = SEVERITY_COLOR[i.severity] || "#94a3b8";
              return (
                <div key={i.din} data-testid={`watchlist-row-${i.din}`} style={{ background: "#080c12", border: `1px solid ${c}33`, borderRadius: 8, padding: 12 }}>
                  <div className="flex items-center justify-between flex-wrap" style={{ gap: 10 }}>
                    <div className="flex items-center gap-3" style={{ minWidth: 0, flex: 1 }}>
                      <Eye size={14} style={{ color: c, flexShrink: 0 }} />
                      <div style={{ minWidth: 0 }}>
                        <div className="flex items-center gap-2 flex-wrap">
                          <code style={{ color: "#a855f7", fontSize: 12 }}>DIN {i.din}</code>
                          <span style={{ fontSize: 12, fontWeight: 600 }}>{i.name || "—"}</span>
                          <span style={pill(c)}>{i.severity}</span>
                          {i.alert_count > 0 && <span style={pill("#ef4444")}>{i.alert_count} alerts</span>}
                        </div>
                        <div style={{ fontSize: 10, color: "#64748b", marginTop: 3 }}>
                          {i.snapshot_count} cos tracked · last scan {i.last_scanned_at ? new Date(i.last_scanned_at).toLocaleString() : "never"}
                          {i.reason && <> · <em>{i.reason}</em></>}
                        </div>
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 5, flexShrink: 0 }}>
                      <button data-testid={`scan-din-${i.din}`} onClick={() => scanOne(i.din)} disabled={scanningOne === i.din} style={{ ...ghostBtn, color: "#3b82f6", borderColor: "#3b82f655" }}>
                        {scanningOne === i.din ? <Loader2 className="animate-spin" size={11} /> : <RefreshCw size={11} />} Scan
                      </button>
                      <button onClick={() => navigate(`/mca21/directors?prefill=${encodeURIComponent(i.name || i.din)}`)} style={ghostBtn} title="Open in search">
                        <ArrowUpRight size={11} />
                      </button>
                      <button data-testid={`remove-din-${i.din}`} onClick={() => removeDin(i.din)} style={{ ...ghostBtn, color: "#ef4444", borderColor: "#ef444455" }}>
                        <Trash2 size={11} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, color, testid }) {
  return (
    <div style={CARD} data-testid={testid}>
      <div className="flex items-center gap-2 mb-2">
        <Icon size={14} style={{ color }} />
        <span style={LABEL}>{label}</span>
      </div>
      <div style={{ fontSize: 26, fontWeight: 800, color }}>{value}</div>
    </div>
  );
}

function Input({ label, value, onChange, placeholder, mono, testid }) {
  return (
    <div>
      <div style={LABEL}>{label}</div>
      <input
        data-testid={testid}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        style={{ ...inputStyle, fontFamily: mono ? "ui-monospace, monospace" : "inherit", marginTop: 4 }}
      />
    </div>
  );
}

const primaryBtn = { background: "linear-gradient(135deg, #ef4444, #dc2626)", color: "#fff", border: "none", padding: "8px 14px", borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 };
const ghostBtn = { background: "transparent", border: "1px solid #1e2530", borderRadius: 6, padding: "6px 11px", color: "#cbd5e1", fontSize: 11, fontWeight: 600, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 5 };
const inputStyle = { width: "100%", background: "#080c12", border: "1px solid #1e2530", borderRadius: 6, padding: "8px 11px", color: "#f1f5f9", fontSize: 13, outline: "none", boxSizing: "border-box" };
const pill = (color) => ({ fontSize: 9, padding: "2px 7px", borderRadius: 3, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase", color, background: `${color}18`, border: `1px solid ${color}55` });
